import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

/**
 * Editorial hero pane used by the Login and Register pages. Warm cream
 * canvas with the Fount brand mark, a large Bricolage headline, and a
 * live-preview brief card that mirrors the actual product experience.
 */
export default function AuthEditorial({ kicker, headline, subline }) {
  return (
    <div
      className="hidden lg:flex relative overflow-hidden flex-col justify-between p-14"
      style={{
        background:
          "radial-gradient(1000px 600px at 12% 15%, rgba(124,58,237,0.10), transparent 55%)," +
          "radial-gradient(900px 500px at 88% 85%, rgba(249,115,22,0.10), transparent 55%)," +
          "var(--bg-primary)",
      }}
    >
      {/* Grain overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
        }}
      />

      {/* Brand mark */}
      <Link to="/" className="relative inline-flex items-center gap-3 group w-fit" data-testid="auth-brand">
        <span className="inline-flex items-end gap-1" aria-hidden>
          <span style={{ display: "inline-block", width: 6, height: 22, background: "#7C3AED", borderRadius: 2 }} />
          <span style={{ display: "inline-block", width: 6, height: 17, background: "#EC4899", borderRadius: 2 }} />
          <span style={{ display: "inline-block", width: 6, height: 12, background: "#F97316", borderRadius: 2 }} />
        </span>
        <span className="font-display text-2xl tracking-[-0.02em] font-semibold leading-none">Fount Stories</span>
      </Link>

      {/* Editorial hero */}
      <div className="relative">
        <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#7C3AED] font-semibold">
          {kicker}
        </div>
        <h2 className="font-display text-5xl xl:text-6xl 2xl:text-7xl tracking-[-0.035em] leading-[0.98] font-semibold mt-5 text-black">
          {headline}
        </h2>
        <p className="text-lg text-gray-700 mt-6 max-w-md leading-relaxed">
          {subline}
        </p>

        {/* Product preview card — mini brief mockup */}
        <div className="relative mt-10 max-w-md">
          <div
            className="rounded-2xl border border-[var(--border)] p-5 bg-white shadow-[0_30px_80px_-25px_rgba(15,23,42,0.15)]"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EC4899]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gray-500 font-semibold">
                Fount Daily Brief · 06:00 UTC
              </div>
            </div>
            <div className="font-display text-xl tracking-tight font-semibold leading-snug">
              Spotify tests AI-hosted podcasts in three new markets
            </div>
            <div className="text-[13px] text-gray-600 mt-2 leading-relaxed">
              A limited pilot lets creators generate a synthetic co-host trained on their own back catalogue.
            </div>
            <div className="mt-4 border-l-2 border-[#7C3AED] pl-3 space-y-1.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#7C3AED] font-bold">
                Talking points
              </div>
              <div className="text-[13px] text-gray-800 leading-snug">
                <span className="text-[#7C3AED] font-mono font-bold mr-1">01</span>
                What authenticity means when your host is code.
              </div>
              <div className="text-[13px] text-gray-800 leading-snug">
                <span className="text-[#7C3AED] font-mono font-bold mr-1">02</span>
                The voice-cloning consent gap for indie creators.
              </div>
              <div className="text-[13px] text-gray-800 leading-snug">
                <span className="text-[#7C3AED] font-mono font-bold mr-1">03</span>
                Who owns your listener data when Spotify hosts you?
              </div>
            </div>
          </div>

          {/* Floating chip */}
          <div className="absolute -top-3 -right-3 bg-white border border-[var(--border)] rounded-full pl-2 pr-3 py-1.5 flex items-center gap-1.5 shadow-sm rotate-2">
            <Sparkles size={12} strokeWidth={2} className="text-[#F97316]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-700 font-semibold">
              2h ago
            </span>
          </div>
        </div>
      </div>

      {/* Footer proof */}
      <div className="relative flex items-center gap-6 pt-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gray-500">
          Signal · Not noise
        </div>
        <div className="h-px flex-1 bg-[var(--border)]" />
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Feed live · 72h window
        </div>
      </div>
    </div>
  );
}
